import React from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import classNames from 'classnames';
import { CPage } from '@appcues/component-library';
import { CheckboxSelect, Icon, Select, SortDropdown } from 'next/components/ui';
import SelectSegmentDropDown from 'components/Common/SelectSegmentDropDown';
import SearchBar from 'components/Common/SearchBar';
import {
  getSortOption,
  getFlowStatusDropdownItems,
  getFlowCreatedByDropdownItems,
  getSortOptionsDropdownItems,
} from 'helpers/view';
import {
  setFlowLayoutType,
  setFlowStatusFilter,
  setFlowSegmentFilter,
  setFlowCreatedByFilter,
  setFlowSearchFilter,
  setFlowTagsFilter,
  setFlowSortOption,
} from 'actions/view';
import { FLOW_LAYOUT } from 'constants/view';

const StyledFilterControls = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
  padding: 16px 0;

  ${Select} {
    margin-left: 8px;
    min-width: 200px;
  }

  .view-toggles {
    margin-left: auto;
    display: flex;

    .view-toggle-icon {
      margin: 5px 0 5px 12px;
      cursor: pointer;
      color: ${props => props.theme['$gray-50']};
      font-size: 22px;

      &.selected-layout {
        color: ${props => props.theme.$blue};
      }
    }
  }
`;

const StyledSortControls = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  margin-bottom: 16px;

  p {
    color: ${props => props.theme['$gray-50']};
    margin-bottom: 0;
    margin-right: 0.25rem;
    font-size: 13px;
    font-weight: 700;
    letter-spacing: 0.8px;
    line-height: 1em;
  }

  a.button {
    border: 0px;
    padding: 0 10px;
    font-weight: 400;
    font-size: 16px;
    color: ${props => props.theme['$gray-70']};
  }

  .search-row {
    color: ${props => props.theme['$gray-50']};
  }

  .sort-view-row {
    justify-content: space-between;
    align-items: center;
    margin-bottom: 16px;

    .sort-view-item {
      display: flex;
      align-items: center;

      .flow-list-counter {
        margin-left: 15px;

        h4 {
          font-weight: 600;
          font-size: ${props => props.theme['$font-size-large']};
          margin-top: 0;
          margin-bottom: 5px;
        }

        p {
          margin: 0;
          color: ${props => props.theme['$gray-30']};
        }

        strong {
          font-weight: 600;
        }
      }
    }
  }
`;

const isValidOption = (prevOption, newOption) => {
  return prevOption !== newOption;
};

// This is a temporary hack to make old page headers look like new one
const SecondBar = styled(CPage.SecondBar)`
  position: sticky;
  top: 108px;
  z-index: 3000;
`;

export function StepViewControls({
  actions,
  createdByFilter,
  flowLayout,
  searchFilter,
  segmentFilter,
  sortOption,
  statusFilter,
  tagsDropdownItems,
  tagsFilter,
  users,
}) {
  const setViewtype = type => {
    if (isValidOption(flowLayout, type)) {
      actions.setFlowLayoutType(type);
    }
  };

  const setStatusFilter = status => {
    if (isValidOption(statusFilter, status)) {
      actions.setFlowStatusFilter(status);
    }
  };

  const setSegmentFilter = segmentId => {
    if (isValidOption(segmentFilter, segmentId)) {
      actions.setFlowSegmentFilter(segmentId);
    }
  };

  const setCreatedByFilter = userId => {
    if (isValidOption(createdByFilter, userId)) {
      actions.setFlowCreatedByFilter(userId);
    }
  };

  const setSortOption = selectedSortOption => {
    if (isValidOption(sortOption, selectedSortOption)) {
      actions.setFlowSortOption(selectedSortOption);
    }
  };

  const flowStatusDropdownItems = getFlowStatusDropdownItems();
  const flowCreatedByDropdownItems = getFlowCreatedByDropdownItems(users);
  const sortDropdownOptions = getSortOptionsDropdownItems();
  const sort = getSortOption(sortOption);

  return (
    <>
      <SecondBar>
        <StyledFilterControls>
          <Select
            aria-label="Status filter"
            icon="flag"
            isClearable
            isSearchable={false}
            kind="borderless"
            onChange={option => setStatusFilter(option?.value ?? '')}
            options={flowStatusDropdownItems}
            placeholder="Any Status"
            value={flowStatusDropdownItems.find(
              ({ value }) => value === statusFilter
            )}
          />
          <CheckboxSelect
            aria-label="Tags filter"
            icon="tag"
            kind="borderless"
            onChange={values => {
              const tags = values.map(({ value }) => value);
              actions.setFlowTagsFilter(tags);
            }}
            options={tagsDropdownItems}
            placeholder="Any Tag"
            value={
              tagsFilter
                ? tagsDropdownItems.filter(({ value }) =>
                    tagsFilter.includes(value)
                  )
                : []
            }
          />
          <SelectSegmentDropDown
            aria-label="Segment filter"
            className="filter"
            isClearable
            kind="borderless"
            onChange={setSegmentFilter}
            placeholder="Any Segment"
            shouldSortSegmentsList
            value={segmentFilter || ''}
          />
          <Select
            aria-label="Teammate filter"
            icon="flag"
            isClearable
            kind="borderless"
            onChange={option => setCreatedByFilter(option?.value ?? null)}
            options={flowCreatedByDropdownItems}
            placeholder="Any Teammate"
            value={flowCreatedByDropdownItems.find(
              ({ value }) => value === createdByFilter
            )}
          />
          <div className="view-toggles">
            {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events */}
            <div
              role="radio"
              tabIndex={0}
              aria-checked={flowLayout === FLOW_LAYOUT.GRID}
              onClick={() => setViewtype(FLOW_LAYOUT.GRID)}
              className={classNames(
                'view-toggle-icon',
                flowLayout === FLOW_LAYOUT.GRID && 'selected-layout'
              )}
            >
              <Icon icon="th-large" />
            </div>

            {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events */}
            <div
              role="radio"
              tabIndex={-1}
              aria-checked={flowLayout === FLOW_LAYOUT.LIST}
              onClick={() => setViewtype(FLOW_LAYOUT.LIST)}
              className={classNames(
                'view-toggle-icon',
                flowLayout === FLOW_LAYOUT.LIST && 'selected-layout'
              )}
            >
              <Icon icon="th-list" />
            </div>
          </div>
        </StyledFilterControls>
      </SecondBar>
      <CPage.Container>
        <StyledSortControls>
          <div className="sort-view-item">
            <SearchBar
              className="search-row"
              placeholder="Search flows"
              value={searchFilter || ''}
              handleNewSearchValue={searchValue =>
                actions.setFlowSearchFilter(searchValue)
              }
            />
          </div>
          <div className="sort-view-item">
            <SortDropdown
              onChange={({ value }) => setSortOption(value)}
              options={sortDropdownOptions}
              value={sort}
            />
          </div>
        </StyledSortControls>
      </CPage.Container>
    </>
  );
}

StepViewControls.propTypes = {
  actions: PropTypes.objectOf(PropTypes.func),
  createdByFilter: PropTypes.string,
  flowLayout: PropTypes.oneOf([FLOW_LAYOUT.LIST, FLOW_LAYOUT.GRID]),
  searchFilter: PropTypes.string,
  segmentFilter: PropTypes.string,
  sortOption: PropTypes.string,
  statusFilter: PropTypes.string,
  tagsDropdownItems: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string,
      value: PropTypes.string,
    })
  ),
  tagsFilter: PropTypes.arrayOf(PropTypes.string),
  users: PropTypes.objectOf(PropTypes.object),
};

function mapDispatchToProps(dispatch) {
  return {
    actions: bindActionCreators(
      {
        setFlowCreatedByFilter,
        setFlowLayoutType,
        setFlowSearchFilter,
        setFlowSegmentFilter,
        setFlowSortOption,
        setFlowStatusFilter,
        setFlowTagsFilter,
      },
      dispatch
    ),
  };
}

export default connect(null, mapDispatchToProps)(StepViewControls);
